/**
 * TermsPageSkeleton - Skeleton loader for terms/waivers page
 *
 * Matches TermsTableOfContents + WaiversList collapsed layout.
 *
 * @example
 * ```tsx
 * <TermsPageSkeleton />
 * <TermsPageSkeleton showHeading={false} showStatus />
 * ```
 */

"use client";

import { Box, Flex, Skeleton } from "@chakra-ui/react";
import { MicroSiteHeaderSkeleton } from "@/components/feedback";
import { TermsMemberNavSkeleton } from "../member/navigation";
import { useThemeTokens } from "@/lib/theme/tokens";

interface TermsPageSkeletonProps {
  /**
   * Whether to show the header skeleton (use false when page has its own header)
   * @default true
   */
  showHeading?: boolean;
  /**
   * Whether to show the "Jump to" table of contents skeleton
   * @default true
   */
  showTableOfContents?: boolean;
  /**
   * Whether waiver cards include signed/status placeholders (member portal)
   * @default false
   */
  showStatus?: boolean;
  /**
   * Member portal sidebar layout (replaces table of contents skeleton)
   * @default false
   */
  showSidebarLayout?: boolean;
  /**
   * Public marketing editorial layout
   * @default false
   */
  variant?: "public" | "portal";
}

function PublicHeroSkeleton() {
  return (
    <Box>
      <Skeleton height="12px" width="96px" mb={3} borderRadius="4px" />
      <Skeleton height={{ base: "34px", md: "42px" }} width="72%" mb={3} borderRadius="6px" />
      <Skeleton height="16px" width="100%" mb={2} borderRadius="4px" />
      <Skeleton height="16px" width="88%" borderRadius="4px" />
    </Box>
  );
}

function TermsTableOfContentsSkeleton({ variant = "portal" }: { variant?: "public" | "portal" }) {
  const theme = useThemeTokens();

  return (
    <Box
      py={variant === "public" ? { base: 4, md: 5 } : 4}
      borderTopWidth="1px"
      borderBottomWidth="1px"
      borderColor={variant === "public" ? theme.borderGlass.subtle : theme.borderGlass.default}
    >
      <Skeleton
        height="12px"
        width={variant === "public" ? "88px" : "56px"}
        mb={3}
        borderRadius="4px"
      />
      <Flex direction="column" gap={variant === "public" ? 0 : 2}>
        {[0.72, 0.58, 0.64].map((widthFactor, index) => (
          <Box
            key={index}
            py={variant === "public" ? 3 : 0}
            borderBottomWidth={variant === "public" && index < 2 ? "1px" : 0}
            borderColor={theme.borderGlass.subtle}
          >
            <Skeleton
              height="16px"
              width={`${Math.round(widthFactor * 100)}%`}
              maxW="320px"
              borderRadius="4px"
            />
          </Box>
        ))}
      </Flex>
    </Box>
  );
}

function PublicWaiverSectionSkeleton({ isLast = false }: { isLast?: boolean }) {
  const theme = useThemeTokens();

  return (
    <Box
      py={{ base: 6, md: 8 }}
      borderBottomWidth={isLast ? 0 : "1px"}
      borderColor={theme.borderGlass.subtle}
    >
      <Skeleton height="12px" width="56px" mb={3} borderRadius="4px" />
      <Skeleton height={{ base: "28px", md: "32px" }} width="78%" mb={5} borderRadius="6px" />
      <Flex direction="column" gap={2}>
        {Array.from({ length: 8 }).map((_, lineIndex) => (
          <Skeleton
            key={lineIndex}
            height="16px"
            width={
              lineIndex === 7
                ? "75%"
                : lineIndex % 3 === 0
                  ? "96%"
                  : lineIndex % 2 === 0
                    ? "88%"
                    : "100%"
            }
            borderRadius="4px"
          />
        ))}
      </Flex>
      <Skeleton height="14px" width="80px" mt={4} borderRadius="4px" />
    </Box>
  );
}

function WaiverCardSkeleton({
  showStatus,
  variant,
}: {
  showStatus: boolean;
  variant: number;
}) {
  const theme = useThemeTokens();
  const showSignedBadge = showStatus && variant === 0;
  const showAcceptedDate = showStatus && variant === 0;
  const showPendingBadge = showStatus && variant === 1;

  return (
    <Box
      p={6}
      borderRadius="8px"
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      bg="none"
      boxShadow={theme.shadow.card}
    >
      <Flex justify="space-between" align="start" mb={4} gap={4}>
        <Skeleton
          height={variant === 2 ? "24px" : "28px"}
          width={variant === 0 ? "68%" : variant === 1 ? "58%" : "74%"}
          borderRadius="4px"
          flex={1}
        />
        {showSignedBadge ? (
          <Skeleton height="24px" width="72px" borderRadius="full" flexShrink={0} />
        ) : null}
        {showPendingBadge ? (
          <Skeleton height="24px" width="120px" borderRadius="full" flexShrink={0} />
        ) : null}
      </Flex>

      {showAcceptedDate ? (
        <Flex align="center" gap={2} mb={4}>
          <Skeleton boxSize="14px" borderRadius="4px" flexShrink={0} />
          <Skeleton height="14px" width="240px" maxW="100%" borderRadius="4px" />
        </Flex>
      ) : null}

      <Box height="1px" bg={theme.borderGlass.default} mb={4} />

      <Flex direction="column" gap={2}>
        {Array.from({ length: 8 }).map((_, lineIndex) => (
          <Skeleton
            key={lineIndex}
            height="16px"
            width={
              lineIndex === 7
                ? "75%"
                : lineIndex % 3 === 0
                  ? "96%"
                  : lineIndex % 2 === 0
                    ? "88%"
                    : "100%"
            }
            borderRadius="4px"
          />
        ))}
      </Flex>

      {variant !== 2 ? (
        <Skeleton height="20px" width="80px" mt={3} borderRadius="4px" />
      ) : null}
    </Box>
  );
}

export const TermsPageSkeleton = ({
  showHeading = true,
  showTableOfContents = true,
  showStatus = false,
  showSidebarLayout = false,
  variant = showStatus || showSidebarLayout ? "portal" : "public",
}: TermsPageSkeletonProps) => {
  const theme = useThemeTokens();
  const isPublicLayout = variant === "public";

  const portalContent = (
    <Flex direction="column" rowGap={6}>
      {[0, 1, 2].map((cardVariant) => (
        <WaiverCardSkeleton
          key={cardVariant}
          showStatus={showStatus}
          variant={cardVariant}
        />
      ))}
    </Flex>
  );

  const publicContent = (
    <Box>
      <Skeleton height="12px" width="100px" mb={4} borderRadius="4px" />
      <Box borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
        {[0, 1, 2].map((sectionIndex) => (
          <PublicWaiverSectionSkeleton key={sectionIndex} isLast={sectionIndex === 2} />
        ))}
      </Box>
    </Box>
  );

  const content = isPublicLayout ? publicContent : portalContent;

  return (
    <Flex
      direction="column"
      gap={{ base: 6, md: 8 }}
      maxW={isPublicLayout ? "720px" : "1044px"}
      mx="auto"
      w="100%"
      data-testid="terms-page-skeleton"
    >
      {showHeading ? (
        isPublicLayout ? <PublicHeroSkeleton /> : <MicroSiteHeaderSkeleton />
      ) : null}

      {isPublicLayout ? (
        <Box pt={1} borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
          <Skeleton height="16px" width="92%" borderRadius="4px" />
        </Box>
      ) : null}

      {showSidebarLayout ? (
        <Flex
          align="flex-start"
          gap={6}
          direction={{ base: "column", lg: "row" }}
          w="100%"
          minW={0}
        >
          <TermsMemberNavSkeleton />
          <Box flex={1} minW={0} w="100%">
            {content}
          </Box>
        </Flex>
      ) : (
        <>
          {showTableOfContents && !isPublicLayout ? (
            <TermsTableOfContentsSkeleton variant="portal" />
          ) : null}
          {content}
        </>
      )}
    </Flex>
  );
};
